import React, { useState, useEffect, useRef } from 'react';
import { Languages, X, ChevronDown, ArrowRight } from 'lucide-react';
import { LANGUAGES, getLanguageById } from './languageConfig';
import { getFlagDisplay } from './FlagEmoji';

// Language Dropdown Component
function LanguageDropdown({ label, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const language = getLanguageById(value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="flex-1" ref={dropdownRef}>
      <label className="text-xs text-gray-400 mb-1.5 block">{label}</label>
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="w-full flex items-center justify-between bg-[#0a1628] border border-white/20 rounded-lg px-4 py-2.5 text-white text-sm hover:bg-white/10 transition-colors"
        >
          <span className="flex items-center gap-2">
            <span className="text-base">{getFlagDisplay(language?.flag, language?.shortCode)}</span>
            <span>{language?.label}</span>
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-[#0a1628] border border-white/20 rounded-lg shadow-xl z-[10001] max-h-48 overflow-y-auto">
            {LANGUAGES.map(lang => (
              <button
                key={lang.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(lang.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors ${
                  value === lang.id ? 'bg-[#6a1fbf]/30 text-white' : 'text-gray-300'
                }`}
              >
                <span className="text-base">{getFlagDisplay(lang.flag, lang.shortCode)}</span>
                <span>{lang.label}</span>
                {value === lang.id && <span className="ml-auto text-[#00c853]">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Translation Service
async function translateText(text, sourceLang, targetLang) {
  if (sourceLang === targetLang || !text.trim()) {
    return text;
  }

  const source = getLanguageById(sourceLang);
  const target = getLanguageById(targetLang);

  if (!source || !target) {
    return text;
  }

  try {
    // Use Google Translate API (free tier - limited)
    const response = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${source.code}&tl=${target.code}&dt=t&q=${encodeURIComponent(text)}`
    );
    const data = await response.json();

    if (data && data[0]) {
      return data[0].map(item => item[0]).join('');
    }
    return text;
  } catch (error) {
    console.error('Translation error:', error);
    return text;
  }
}

export default function RealTimeTranslatorModal({
  isOpen,
  onClose,
  sourceLanguage = 'en',
  targetLanguage = 'en',
  onSourceChange,
  onTargetChange
}) {
  const modalRef = useRef(null);
  const [inputText, setInputText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const translateTimeoutRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    if (isOpen) {
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Auto-translate as user types (debounced)
  useEffect(() => {
    if (translateTimeoutRef.current) {
      clearTimeout(translateTimeoutRef.current);
    }

    if (!inputText.trim()) {
      setTranslatedText('');
      return;
    }

    if (sourceLanguage === targetLanguage) {
      setTranslatedText(inputText);
      return;
    }

    setIsTranslating(true);

    translateTimeoutRef.current = setTimeout(async () => {
      const result = await translateText(inputText, sourceLanguage, targetLanguage);
      setTranslatedText(result);
      setIsTranslating(false);
    }, 500);

    return () => {
      if (translateTimeoutRef.current) {
        clearTimeout(translateTimeoutRef.current);
      }
    };
  }, [inputText, sourceLanguage, targetLanguage]);

  // Clear translation when languages change
  useEffect(() => {
    if (inputText.trim() && sourceLanguage !== targetLanguage) {
      setIsTranslating(true);
      translateText(inputText, sourceLanguage, targetLanguage).then(result => {
        setTranslatedText(result);
        setIsTranslating(false);
      });
    }
  }, [sourceLanguage, targetLanguage]);

  if (!isOpen) return null;

  const characterCount = inputText.length;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="w-full max-w-3xl mx-4 bg-[#001233]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
              <Languages className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Real-Time Translator</h2>
              <p className="text-xs text-gray-400">Type to see instant translations</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Language Selectors */}
        <div className="px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-4">
            <LanguageDropdown
              label="Source Language"
              value={sourceLanguage}
              onChange={onSourceChange}
            />
            <div className="flex items-center justify-center pt-6">
              <ArrowRight className="w-5 h-5 text-gray-500" />
            </div>
            <LanguageDropdown
              label="Target Language"
              value={targetLanguage}
              onChange={onTargetChange}
            />
          </div>
        </div>

        {/* Translation Area */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4">
            {/* Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-white font-medium">Your Text</label>
                <span className="text-xs text-gray-500">{characterCount} characters</span>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type or paste text here..."
                className="w-full h-40 bg-[#0a1628] border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 resize-none focus:outline-none focus:border-[#6a1fbf]/50 transition-colors"
              />
            </div>

            {/* Output */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-white font-medium">Translation</label>
                {isTranslating && (
                  <span className="text-xs text-[#6a1fbf] animate-pulse">Translating...</span>
                )}
              </div>
              <div className="w-full h-40 bg-[#0a1628] border border-white/20 rounded-xl px-4 py-3 overflow-y-auto">
                {translatedText ? (
                  <p className="text-white text-sm">{translatedText}</p>
                ) : (
                  <p className="text-gray-500 text-sm">Translation will appear here...</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-white/5 border-t border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs text-gray-500">Real-time translation active</span>
          </div>
          <span className="text-xs text-gray-600">Powered by AI Translation</span>
        </div>
      </div>
    </div>
  );
}
